import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useShiftRouteEstimate } from "@src/appV2/redesign/ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import { useDrivingDistanceToWorkplace } from "@src/appV2/redesign/Workplace/useDrivingDistanceToWorkplace";
import { addMinutes, isBefore, secondsToMinutes, subHours } from "date-fns";

import type { ArrivalContext } from "../types";

interface UseTravelTimeParams {
  arrivalContext: ArrivalContext;
  facilityCoordinates?: [number, number];
  workerId?: string;
  shiftId?: string;
  workplaceId?: string;
  shiftStartIso?: string;
}

/**
 * Single-responsibility hook for calculating travel time
 * Only calculates when we have a travel location and facility coordinates
 */
export function useTravelTime({
  arrivalContext,
  facilityCoordinates,
  workerId,
  shiftId,
  workplaceId,
  shiftStartIso,
}: UseTravelTimeParams) {
  const useRouteEstimates = useCbhFlag(CbhFeatureFlag.ROUTE_ESTIMATES_REPLACE_DRIVING_TIME, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ROUTE_ESTIMATES_REPLACE_DRIVING_TIME],
  });

  const facilityGeoLocation = isDefined(facilityCoordinates)
    ? convertToGeoLocation(facilityCoordinates)
    : undefined;

  const travelFromGeoLocation = arrivalContext.travelFromLocation
    ? {
        latitude: arrivalContext.travelFromLocation.lat,
        longitude: arrivalContext.travelFromLocation.lng,
      }
    : undefined;

  const shouldCalculateDistance =
    isDefined(facilityGeoLocation) &&
    isDefined(travelFromGeoLocation) &&
    (arrivalContext.state === "traveling" || arrivalContext.state === "ready-to-travel");

  const {
    data: distanceData,
    isLoading: isLoadingDrivingDistance,
    error: drivingDistanceError,
  } = useDrivingDistanceToWorkplace({
    workplaceGeoLocation: facilityGeoLocation,
    workerGeoLocation: travelFromGeoLocation,
    enabled: !useRouteEstimates && shouldCalculateDistance,
  });
  const googleEstimatedTravelTimeMinutes = isDefined(distanceData?.duration?.value)
    ? Math.ceil(secondsToMinutes(distanceData.duration.value))
    : undefined;

  let departureTime = isDefined(shiftStartIso) ? subHours(new Date(shiftStartIso), 1) : undefined;

  if (isDefined(departureTime) && isBefore(departureTime, new Date())) {
    departureTime = addMinutes(new Date(), 5);
  }

  const {
    data: routeEstimateData,
    isLoading: isLoadingRouteEstimate,
    error: routeEstimateError,
  } = useShiftRouteEstimate(
    {
      source: travelFromGeoLocation,
      destination: facilityGeoLocation,
      provider: "AWS_LOCATION",
      departureTimeIso: departureTime?.toISOString(),
      shiftId,
      workerId: workerId ?? "",
      workplaceId: workplaceId ?? "",
      shiftStartIso,
      onSuccess: (data) => {
        logEvent(APP_V2_APP_EVENTS.ESTIMATED_TRAVEL_TIME_CALCULATED, {
          durationInMinutes: data?.durationInMinutes,
          workerId: workerId ?? "",
          shiftId: shiftId ?? "",
          workplaceId: workplaceId ?? "",
          shiftStartIso: shiftStartIso ?? "",
        });
      },
      onError: () => {
        logEvent(APP_V2_APP_EVENTS.ESTIMATED_TRAVEL_TIME_CALCULATION_FAILED, {
          error: routeEstimateError,
          workerId: workerId ?? "",
          shiftId: shiftId ?? "",
          workplaceId: workplaceId ?? "",
          shiftStartIso: shiftStartIso ?? "",
        });
      },
    },
    {
      enabled:
        useRouteEstimates &&
        shouldCalculateDistance &&
        isDefined(workerId) &&
        isDefined(workplaceId),
    }
  );

  const estimatedTravelTime = useRouteEstimates
    ? routeEstimateData?.durationInMinutes
    : googleEstimatedTravelTimeMinutes;

  const isLoading = useRouteEstimates ? isLoadingRouteEstimate : isLoadingDrivingDistance;

  return {
    estimatedTravelTime,
    isLoading,
    travelFromType: arrivalContext.travelFromLocation?.type,
    error: drivingDistanceError ?? routeEstimateError,
  };
}
